import { Theme } from "../../../../providers/ThemeProvider";
import CustomSelect from "../common/CustomSelect";
import useTheme from "../../../../common/hooks/useTheme";
import React from "react";
import NumericInputField from "../common/NumericInputField";
import { Box, Typography } from "@mui/material";
import CustomAutoComplete from "../common/CustomAutoComplete";
import CustomDateRangePicker from "../common/CustomDateRangePicker";
import ScrollableWrapper from "../common/ScrollableWrapper";
import {
  enSvcUDurationCategory,
  enTimeUnit,
  formatTimeUnitWithParen,
} from "./helper";
import { addDays, format } from "date-fns";
import { ServiceDetails } from "./types";

type Props = {
  serviceDetails: ServiceDetails;
  setServiceDetails: (
    value: ServiceDetails | ((prev: ServiceDetails) => ServiceDetails)
  ) => void;
};

// Time unit keys allowed for pre-appointment notice (minutes through weeks)
const ALLOWED_TIME_UNIT_KEYS = [2, 3, 4, 5] as const;

const ServiceBuilderStep3 = ({ serviceDetails, setServiceDetails }: Props) => {
  const theme: Theme = useTheme();
  const dateRangesOptions = Object.values(enSvcUDurationCategory);

  // Helper function to get duration category display value
  const getSelectedDateRange = () => {
    const category = serviceDetails?.durationCategory;
    return category && category > 0
      ? enSvcUDurationCategory[category as keyof typeof enSvcUDurationCategory]
      : null;
  };

  // Helper function to get calendar date range
  const getCalendarDateRange = () => {
    const from = serviceDetails?.calendarRangeFrom;
    const to = serviceDetails?.calendarRangeTo;
    return {
      startDate: from && from !== "0001-01-01T00:00:00" ? new Date(from) : null,
      endDate: to && to !== "0001-01-01T00:00:00" ? new Date(to) : null,
    };
  };

  // Handler for duration category change
  const handleDateRangeChange = (value: string | null) => {
    const categoryKey = Object.keys(enSvcUDurationCategory).find(
      (key) =>
        enSvcUDurationCategory[
          Number(key) as keyof typeof enSvcUDurationCategory
        ] === value
    );
    const categoryNumber = categoryKey ? Number(categoryKey) : 0;

    setServiceDetails({
      ...serviceDetails,
      durationCategory: categoryNumber,
      // Reset other category fields
      durationValue: categoryNumber === 1 ? serviceDetails.durationValue : null,
      durationType: categoryNumber === 1 ? serviceDetails.durationType : 0,
      calendarRangeFrom:
        categoryNumber === 2
          ? serviceDetails.calendarRangeFrom
          : "0001-01-01T00:00:00",
      calendarRangeTo:
        categoryNumber === 2
          ? serviceDetails.calendarRangeTo
          : "0001-01-01T00:00:00",
    });
  };

  // Generic handler for updating duration fields
  const updateDurationField = (field: string, value: any) => {
    setServiceDetails({
      ...serviceDetails,
      [field]: value,
    });
  };

  // Handler for fixed duration value
  const handleFixedDurationChange = (value: string) => {
    const numericValue = parseInt(value);
    const validValue =
      isNaN(numericValue) || numericValue < 0 ? 0 : numericValue;
    updateDurationField("durationValue", validValue);
  };

  // Handler for calendar date range
  const handleCalendarDateRangeChange = (dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    const formatDateForBackend = (date: Date | null) => {
      if (!date) return "0001-01-01T00:00:00";
      // Create date in local timezone to avoid UTC conversion issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}T00:00:00`;
    };

    setServiceDetails({
      ...serviceDetails,
      calendarRangeFrom: formatDateForBackend(dateRange.startDate),
      calendarRangeTo: formatDateForBackend(dateRange.endDate),
    });
  };

  // Generic handler for numeric fields with validation
  const handleNumericFieldChange = (field: string) => (value: string) => {
    const numericValue = parseInt(value);
    const validValue =
      isNaN(numericValue) || numericValue < 0 ? 0 : numericValue;
    updateDurationField(field, validValue);
  };

  // Create specific handlers using the generic function
  const handleBufferTimeBeforeChange =
    handleNumericFieldChange("bufferTimeBefore");
  const handleBufferTimeAfterChange =
    handleNumericFieldChange("bufferTimeAfter");
  const handlePreAppointmentNoticeChange = handleNumericFieldChange(
    "preAppointmentNotice"
  );

  // Handler for pre-appointment notice type
  const handlePreAppointmentNoticeTypeChange = (value: string | null) => {
    const noticeTypeKey = ALLOWED_TIME_UNIT_KEYS.find(
      (key) => formatTimeUnitWithParen(enTimeUnit[key]) === value
    );
    updateDurationField("preAppointmentNoticeType", noticeTypeKey ?? 0);
  };

  // Helper to get notice type display value
  const getSelectedNoticeType = () => {
    const noticeType = serviceDetails?.preAppointmentNoticeType;
    return noticeType && noticeType > 0 && enTimeUnit[noticeType]
      ? formatTimeUnitWithParen(enTimeUnit[noticeType])
      : null;
  };

  return (
    <ScrollableWrapper>
      <div style={styles.content}>
        <div>
          <div style={styles.title(theme)}>Date range</div>
          <CustomSelect
            label="Select"
            options={dateRangesOptions}
            value={getSelectedDateRange()}
            onChange={handleDateRangeChange}
          />
          {getSelectedDateRange() === "Calendar range" && (
            <div style={styles.inputsContainer}>
              <CustomDateRangePicker
                label="Date range"
                value={getCalendarDateRange()}
                onChange={handleCalendarDateRangeChange}
              />
            </div>
          )}
        </div>
        <Box>
          <Typography sx={styles.title(theme)}>
            Buffer time{" "}
            <span style={styles.subtitle(theme)}>(Session spacing)</span>
          </Typography>
          <Box sx={styles.inputsContainer}>
            <Box sx={styles.inputWrapper}>
              <NumericInputField
                label="Before event"
                value={serviceDetails?.bufferTimeBefore?.toString() || ""}
                handleChange={(e) =>
                  handleBufferTimeBeforeChange(e.target.value)
                }
                unit={"min"}
                allowNegative={false}
              />
            </Box>
            <Box sx={styles.inputWrapper}>
              <NumericInputField
                label="After event"
                value={serviceDetails?.bufferTimeAfter?.toString() || ""}
                handleChange={(e) =>
                  handleBufferTimeAfterChange(e.target.value)
                }
                unit={"min"}
                allowNegative={false}
              />
            </Box>
          </Box>
        </Box>
        <Box>
          <Typography sx={styles.title(theme)}>
            Pre-appointment notice
          </Typography>
          <Box sx={styles.inputsContainer}>
            <Box sx={styles.inputWrapper}>
              <NumericInputField
                label="Value"
                value={serviceDetails?.preAppointmentNotice?.toString() || ""}
                handleChange={(e) =>
                  handlePreAppointmentNoticeChange(e.target.value)
                }
                allowNegative={false}
              />
            </Box>
            <CustomAutoComplete
              label="Duration"
              options={ALLOWED_TIME_UNIT_KEYS.map((key) =>
                formatTimeUnitWithParen(enTimeUnit[key])
              )}
              value={getSelectedNoticeType()}
              onChange={handlePreAppointmentNoticeTypeChange}
              placeholder="Select unit"
            />
          </Box>
        </Box>
      </div>
    </ScrollableWrapper>
  );
};

// Standalone validation function for Step 3
export const validateStep3 = (
  serviceDetails: ServiceDetails
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // 1. Date Range - Duration Category must be selected
  if (
    !serviceDetails.durationCategory ||
    serviceDetails.durationCategory <= 0
  ) {
    errors.push("Please select a date range category");
  }

  // 2. Fixed Duration validation (when Fixed duration is selected)
  if (serviceDetails.durationCategory === 1) {
    if (!serviceDetails.durationValue || serviceDetails.durationValue <= 0) {
      errors.push("Please enter a valid duration value");
    }
    if (!serviceDetails.durationType || serviceDetails.durationType <= 0) {
      errors.push("Please select a duration type");
    }
  }

  // 3. Calendar Range validation (when Calendar range is selected)
  if (serviceDetails.durationCategory === 2) {
    const hasValidStartDate =
      serviceDetails.calendarRangeFrom &&
      serviceDetails.calendarRangeFrom !== "0001-01-01T00:00:00";
    const hasValidEndDate =
      serviceDetails.calendarRangeTo &&
      serviceDetails.calendarRangeTo !== "0001-01-01T00:00:00";

    if (!hasValidStartDate) {
      errors.push("Please select a start date");
    }
    if (!hasValidEndDate) {
      errors.push("Please select an end date");
    }

    // Check if end date is after start date
    if (hasValidStartDate && hasValidEndDate) {
      const startDate = new Date(serviceDetails.calendarRangeFrom);
      const endDate = new Date(serviceDetails.calendarRangeTo);
      if (endDate <= startDate) {
        errors.push("End date must be after start date");
      }
    }
  }

  // 4. Buffer Time validation (always required)
  if (
    serviceDetails.bufferTimeBefore === null ||
    serviceDetails.bufferTimeBefore === undefined ||
    serviceDetails.bufferTimeBefore < 0
  ) {
    errors.push("Please enter a valid buffer time before value");
  }
  if (
    serviceDetails.bufferTimeAfter === null ||
    serviceDetails.bufferTimeAfter === undefined ||
    serviceDetails.bufferTimeAfter < 0
  ) {
    errors.push("Please enter a valid buffer time after value");
  }

  // 5. Pre-appointment Notice validation (always required)
  if (
    serviceDetails.preAppointmentNotice === null ||
    serviceDetails.preAppointmentNotice === undefined ||
    serviceDetails.preAppointmentNotice < 0
  ) {
    errors.push("Please enter a valid pre-appointment notice value");
  }
  if (
    !serviceDetails.preAppointmentNoticeType ||
    serviceDetails.preAppointmentNoticeType <= 0
  ) {
    errors.push("Please select a pre-appointment notice duration");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export default ServiceBuilderStep3;

const styles = {
  content: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "32px",
    paddingBottom: "20px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    flex: 1,
    marginBottom: "16px",
  }),
  subtitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
  }),
  inputsContainer: { marginTop: "16px", display: "flex", gap: "16px" },
  inputWrapper: {
    flex: 1,
    minWidth: 0,
  },
  availabilityText: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    marginTop: "8px",
    width: "100%",
  }),
};
